/**
 * Google Tag Manager loader (Epic 33, Story 33-3).
 *
 * GTM is the clean line for wiring the Iubenda CMP (Story 33-4) + Consent Mode
 * v2 (Story 33-5) without baking compliance logic into the codebase (ADR-0005
 * Risk #2). The container loads CLIENT-SIDE and DORMANT: until
 * `VITE_GTM_CONTAINER_ID` holds a real `GTM-XXXXXXX` id, nothing loads.
 *
 * Load order (enforced by `initConsentTagManager`): push Consent Mode v2
 * default-DENIED first (33-5) → then the GTM container (33-3). This guarantees
 * tags can never fire ungated, even before the CMP renders.
 */
import {
  pushConsentDefaultDenied,
  hasConsentDefault,
} from "./consent-mode";

export const GTM_CONTAINER_ID: string = import.meta.env.VITE_GTM_CONTAINER_ID ?? "";

const GTM_ID_PATTERN = /^GTM-[A-Z0-9]+$/;

/** True once a real GTM container id is provisioned. */
export function isGtmConfigured(): boolean {
  return GTM_ID_PATTERN.test(GTM_CONTAINER_ID.trim());
}

/** Pure builder for the GTM script src (testable without the DOM). */
export function gtmScriptUrl(containerId: string): string {
  return `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(containerId.trim())}`;
}

function isGtmScriptPresent(): boolean {
  return Boolean(
    document.querySelector('script[src^="https://www.googletagmanager.com/gtm.js"]'),
  );
}

/**
 * Initialize the consent + tag-manager stack once, client-side. Idempotent:
 * guarded on the dataLayer default + the script presence, so multiple mounts
 * (StrictMode double-invoke, route remounts) never duplicate.
 *
 * 1. Consent Mode v2 default-DENIED (33-5) — always, even when GTM is dormant.
 * 2. GTM container (33-3) — only when configured.
 */
export function initConsentTagManager(): void {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  window.dataLayer = window.dataLayer ?? [];

  // 1. Default-denied before anything (no-op if already present).
  if (!hasConsentDefault()) pushConsentDefaultDenied();

  // 2. Load GTM — dormant until provisioned.
  if (!isGtmConfigured() || isGtmScriptPresent()) return;

  window.dataLayer.push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
  const script = document.createElement("script");
  script.async = true;
  script.src = gtmScriptUrl(GTM_CONTAINER_ID);
  document.head.appendChild(script);
}
